import React, { useState } from "react";
import { Tag, Typography } from "antd";
import { MdLocationOn, MdContacts } from "react-icons/md";

import WhatsAppLayout from "components/WhatsApp/WhatsAppLayout";
import WhatsAppText from "components/WhatsApp/WhatsAppText";
import Attachment from "./Attachment";
import HtmlEmailFrame from "./HtmlEmailFrame";
import MessageBubble, { BubbleMeta, MessageInfo } from "./MessageBubble";
import { CHANNELS } from "./constants";
import useAuthedMedia from "./useAuthedMedia";

const { Text } = Typography;

/**
 * One message from the unified envelope, on either channel.
 *
 * The backend normalises WhatsApp rows and email rows into the same shape, so
 * this renders both; only the body differs.
 */

/** A template or interactive message: header (text or file), body, footer, buttons. */
const WhatsAppLayoutBody = ({ message }) => {
  const meta = message.meta || {};
  // The header file, when there is one, is the message's only attachment.
  const file = (message.attachments || [])[0];
  const { url } = useAuthedMedia(file?.url);
  return (
    <>
      <WhatsAppLayout
        layout={meta.layout}
        mediaUrl={url}
        media={file ? { filename: file.filename, mime_type: file.content_type } : null}
      />
      {meta.template ? (
        <Tag color="blue" style={{ marginTop: 4, fontSize: 10 }}>
          Template
        </Tag>
      ) : null}
      <FailureNote message={message} />
    </>
  );
};

/** Why WhatsApp did not deliver it, under the bubble's content. */
const FailureNote = ({ message }) =>
  message.status === "failed" && message.error_message ? (
    <div style={{ marginTop: 6, color: "#ff4d4f", fontSize: 12 }}>{message.error_message}</div>
  ) : null;

export const WhatsAppBody = ({ message }) => {
  const meta = message.meta || {};

  if (meta.layout) return <WhatsAppLayoutBody message={message} />;

  if (message.type === "location" && meta.location) {
    const { latitude, longitude, name, address } = meta.location;
    return (
      <a
        href={`https://www.google.com/maps?q=${latitude},${longitude}`}
        target="_blank"
        rel="noopener noreferrer"
        style={{ display: "flex", gap: 8, alignItems: "center", color: "inherit" }}
      >
        <MdLocationOn style={{ fontSize: 22, color: "#e74c3c" }} />
        <div>
          <div style={{ fontSize: 13 }}>{name || "Shared location"}</div>
          {address ? (
            <Text type="secondary" style={{ fontSize: 11 }}>
              {address}
            </Text>
          ) : null}
        </div>
      </a>
    );
  }

  if (message.type === "contacts" && Array.isArray(meta.contacts)) {
    return (
      <div>
        {meta.contacts.map((entry, index) => (
          <div
            key={index}
            style={{ display: "flex", gap: 8, alignItems: "center", padding: "2px 0" }}
          >
            <MdContacts style={{ fontSize: 18, color: "#54656f" }} />
            <span style={{ fontSize: 13 }}>
              {entry?.name?.formatted_name || entry?.name?.first_name || "Contact"}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <>
      {(message.attachments || []).map((attachment) => (
        <div key={attachment.id} style={{ marginBottom: message.body ? 4 : 0 }}>
          <Attachment attachment={attachment} />
        </div>
      ))}
      {message.body ? (
        <div style={{ fontSize: 14 }}>
          <WhatsAppText text={message.body} />
        </div>
      ) : null}
      {meta.template ? (
        <Tag color="blue" style={{ marginTop: 4, fontSize: 10 }}>
          Template
        </Tag>
      ) : null}
      <FailureNote message={message} />
    </>
  );
};

/** How much of a long email to show before asking. */
const COLLAPSE_AFTER_CHARS = 900;

export const EmailBody = ({ message }) => {
  const [expanded, setExpanded] = useState(false);
  const full = message.body_full || message.body || "";
  const shown = message.body || "";
  // The stripped body drops the quoted thread; offering the original back is
  // what makes an aggressive strip safe.
  const hasMore = full && full.length > shown.length + 20;
  const tooLong = shown.length > COLLAPSE_AFTER_CHARS;
  const visible = expanded || !tooLong ? shown : `${shown.slice(0, COLLAPSE_AFTER_CHARS)}…`;

  const html = message.body_html;

  return (
    <>
      {html ? (
        <HtmlEmailFrame html={html} attachments={message.attachments} />
      ) : (
        <div style={{ fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
          {expanded && hasMore ? full : visible}
        </div>
      )}

      {!html && (tooLong || hasMore) && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          style={{
            border: "none",
            background: "rgba(0,0,0,0.06)",
            borderRadius: 10,
            padding: "1px 10px",
            marginTop: 6,
            cursor: "pointer",
            fontSize: 12,
            color: "#54656f",
          }}
        >
          {expanded ? "Show less" : hasMore ? "··· show quoted text" : "Show more"}
        </button>
      )}

      {(message.attachments || []).filter((a) => !a.is_inline).length > 0 && (
        <div style={{ marginTop: 8, display: "grid", gap: 6 }}>
          {message.attachments
            .filter((attachment) => !attachment.is_inline)
            .map((attachment) => (
              <Attachment key={attachment.id} attachment={attachment} />
            ))}
        </div>
      )}

      {message.error_message ? (
        <div style={{ marginTop: 6, color: "#ff4d4f", fontSize: 12 }}>
          {message.error_message}
        </div>
      ) : null}
    </>
  );
};

const EnvelopeMessage = ({ message, palette }) => {
  const isOutbound = message.direction === "outbound";
  const isEmail = message.channel === CHANNELS.EMAIL;
  const isBareMedia =
    !isEmail && !message.body && (message.attachments || []).length > 0;

  return (
    <MessageBubble
      id={message.id}
      isOutbound={isOutbound}
      palette={palette}
      dense={isBareMedia}
      maxWidth={isEmail ? 680 : 520}
      reaction={message.meta?.reaction?.emoji}
      footer={
        <BubbleMeta
          timestamp={message.sent_at}
          status={message.status}
          isOutbound={isOutbound}
          overlayOnMedia={isBareMedia}
          info={
            isEmail ? null : (
              <MessageInfo
                status={message.status}
                receipts={message.meta?.receipts}
                errorMessage={message.error_message}
                sentAt={message.sent_at}
              />
            )
          }
        />
      }
      header={
        message.is_automated ? (
          <Tag color="default" style={{ fontSize: 10, marginBottom: 4 }}>
            Automated
          </Tag>
        ) : null
      }
    >
      {isEmail ? <EmailBody message={message} /> : <WhatsAppBody message={message} />}
    </MessageBubble>
  );
};

export default EnvelopeMessage;
